/**
 * Projects Admin View
 *
 * Manage i-GlooBot projects: view, create, edit system prompts,
 * manage user assignments, and configure per-project channels.
 */

import { html, nothing } from "lit";
import type {
  CronJobEntry,
  FolderEntry,
  ProjectContactEntry,
  ProjectDetailPanel,
  ProjectSessionEntry,
  ProjectUserEntry,
} from "../controllers/igloobot.ts";
import type { GatewayAgentRow } from "../types.ts";
import type { SkillEntry } from "./skills-admin.ts";
import type { TaskEntry } from "./tasks-admin.ts";
import type { UserEntry } from "./users-admin.ts";

/** Whether the browser supports the native directory picker API. */
const HAS_DIRECTORY_PICKER = typeof window !== "undefined" && "showDirectoryPicker" in window;

/** Build display name from firstName + lastName for ProjectUserEntry. */
function puDisplayName(pu: { firstName?: string | null; lastName?: string | null }): string {
  return [pu.firstName, pu.lastName].filter(Boolean).join(" ") || "";
}

export type ProjectEntry = {
  id: string;
  name: string;
  description: string | null;
  systemPrompt: string;
  isActive: boolean;
  skills: string[];
  slug?: string | null;
  privatePath?: string | null;
  publicPath?: string | null;
  /** @deprecated Use knowledgeBasePaths */
  knowledgeBasePath?: string | null;
  /** @deprecated Use workspacePaths */
  workspacePath?: string | null;
  knowledgeBasePaths?: FolderEntry[];
  workspacePaths?: FolderEntry[];
  agentId?: string | null;
  userCount?: number;
  taskCount?: number;
  createdAt: string;
};

export type ProjectsProps = {
  loading: boolean;
  error: string | null;
  projects: ProjectEntry[];
  selectedProjectId: string | null;
  selectedProject: ProjectEntry | null;
  editMode: boolean;
  editForm: Partial<ProjectEntry> | null;
  sessions: ProjectSessionEntry[];
  sessionsLoading: boolean;
  // Detail panel sub-tabs
  detailPanel: ProjectDetailPanel;
  projectUsers: ProjectUserEntry[];
  projectUsersLoading: boolean;
  projectSkills: SkillEntry[];
  projectSkillsLoading: boolean;
  projectCronJobs: CronJobEntry[];
  projectCronJobsLoading: boolean;
  projectTasks: TaskEntry[];
  projectTasksLoading: boolean;
  projectContacts: ProjectContactEntry[];
  projectContactsLoading: boolean;
  contactCreateMode: boolean;
  contactEditingId: string | null;
  allUsers: UserEntry[];
  sessionCreateMode: boolean;
  agents: GatewayAgentRow[];
  // Callbacks
  onRefresh: () => void;
  onSelectProject: (id: string) => void;
  onCreateProject: () => void;
  onEditProject: (id: string) => void;
  onSaveProject: () => void;
  onCancelEdit: () => void;
  onToggleActive: (id: string, active: boolean) => void;
  onDeleteProject: (id: string) => void;
  onFormChange: (field: string, value: unknown) => void;
  onCompleteSession: (sessionId: string, status: "completed" | "failed") => void;
  onSetDetailPanel: (panel: ProjectDetailPanel) => void;
  // User management
  onAddUser: (userId: string, role: "admin" | "project_admin" | "user") => void;
  onChangeUserRole: (userId: string, role: "admin" | "project_admin" | "user") => void;
  onRemoveUser: (userId: string) => void;
  // Contact management
  onCreateContact: (data: {
    name: string;
    channel: string;
    address: string;
    tags: string[];
    notes: string;
  }) => void;
  onUpdateContact: (
    id: string,
    data: {
      name?: string;
      channel?: string;
      address?: string;
      tags?: string[];
      notes?: string | null;
      isActive?: boolean;
    },
  ) => void;
  onDeleteContact: (id: string) => void;
  onStartCreateContact: () => void;
  onCancelContactForm: () => void;
  onStartEditContact: (id: string) => void;
  // Session management
  onCreateSession: () => void;
  onCancelCreateSession: () => void;
  onSaveNewSession: (type: "chat" | "voice", channel: string) => void;
  // Avatar as a skill
  onToggleAvatar: (enabled: boolean) => void;
  // Agent assignment
  onUpdateProjectAgent: (projectId: string, agentId: string | null) => void;
  // Knowledge Base
  knowledgeBaseSyncing: boolean;
  knowledgeBaseSyncResult: {
    processed: number;
    skipped: number;
    failed: number;
    totalFiles: number;
    errors?: string[];
  } | null;
  onSyncKnowledgeBase: () => void;
  // Workspace
  workspaceVerifying: boolean;
  workspaceVerifyResult: {
    exists: boolean;
    readable: boolean;
    writable: boolean;
    fileCount?: number;
    error?: string;
  } | null;
  onVerifyWorkspace: () => void;
  // Multi-path folder management
  onAddFolderPath: (target: "knowledgeBasePaths" | "workspacePaths", entry: FolderEntry) => void;
  onUpdateFolderPath: (
    target: "knowledgeBasePaths" | "workspacePaths",
    index: number,
    entry: FolderEntry,
  ) => void;
  onRemoveFolderPath: (target: "knowledgeBasePaths" | "workspacePaths", index: number) => void;
  onAddFolderPathDirect: (
    target: "knowledgeBasePaths" | "workspacePaths",
    entry: FolderEntry,
  ) => void;
  onRemoveFolderPathDirect: (
    target: "knowledgeBasePaths" | "workspacePaths",
    index: number,
  ) => void;
};

export function renderProjects(props: ProjectsProps) {
  return html`
    <section class="projects-layout">
      <!-- Project List -->
      <div class="card">
        <div class="card-title">
          Projects
          <span class="muted">(${props.projects.length})</span>
        </div>
        <div class="card-actions" style="display:flex;gap:8px;margin-bottom:12px;">
          <button class="btn btn--sm" @click=${props.onRefresh} ?disabled=${props.loading}>
            ${props.loading ? "Loading..." : "Refresh"}
          </button>
          <button class="btn btn--sm primary" @click=${props.onCreateProject}>
            + New Project
          </button>
        </div>

        ${props.error ? html`<div class="callout danger">${props.error}</div>` : nothing}

        ${
          props.projects.length === 0 && !props.loading
            ? html`
                <div class="muted" style="padding: 16px 0">No projects yet. Create one to get started.</div>
              `
            : nothing
        }

        <div class="project-list">
          ${props.projects.map(
            (project) => html`
              <div
                class="project-item ${props.selectedProjectId === project.id ? "project-item--active" : ""}"
                @click=${() => props.onSelectProject(project.id)}
                style="padding:12px;border:1px solid var(--border);border-radius:8px;margin-bottom:8px;cursor:pointer;"
              >
                <div style="display:flex;justify-content:space-between;align-items:center;">
                  <strong>${project.name}</strong>
                  <span class="pill pill--sm ${project.isActive ? "pill--ok" : ""}">
                    ${project.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
                ${
                  project.description
                    ? html`<div class="muted" style="margin-top:4px;font-size:0.85em;">${project.description}</div>`
                    : nothing
                }
                <div class="muted" style="margin-top:6px;font-size:0.8em;">
                  ${project.userCount ?? 0} users &middot;
                  ${project.taskCount ?? 0} tasks &middot;
                  Created ${project.createdAt.split("T")[0]}
                </div>
              </div>
            `,
          )}
        </div>
      </div>

      <!-- Project Detail / Edit -->
      ${
        props.selectedProject || props.editMode
          ? renderProjectDetail(props)
          : html`
              <div class="card">
                <div class="muted" style="padding: 24px; text-align: center">
                  Select a project to view details, or create a new one.
                </div>
              </div>
            `
      }
    </section>
  `;
}

// --------------- Shared Styles ---------------

const INPUT_STYLE =
  "width:100%;padding:8px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);";
const TEXTAREA_STYLE = `${INPUT_STYLE}font-family:monospace;font-size:0.9em;resize:vertical;`;

const STATUS_COLORS: Record<string, string> = {
  active: "pill--ok",
  completed: "",
  failed: "pill--danger",
  paused: "pill--warn",
  pending: "",
  scheduled: "pill--warn",
  contacted: "pill--warn",
  awaiting_response: "pill--warn",
  in_progress: "pill--ok",
  escalated: "pill--danger",
  cancelled: "",
};

const DETAIL_TABS: { key: ProjectDetailPanel; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "users", label: "Users" },
  { key: "contacts", label: "Contacts" },
  { key: "skills", label: "Skills" },
  { key: "tasks", label: "Tasks" },
  { key: "cron", label: "Cron Jobs" },
  { key: "sessions", label: "Sessions" },
  { key: "knowledge-base", label: "Knowledge Base" },
  { key: "workspace", label: "Workspace" },
];

// --------------- Project Detail ---------------

function renderProjectDetail(props: ProjectsProps) {
  const project = props.editMode ? props.editForm : props.selectedProject;
  if (!project) return nothing;

  return html`
    <div class="card">
      <!-- Header -->
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
        <div>
          <div class="card-title" style="margin-bottom:2px;">
            ${
              props.editMode
                ? project.id
                  ? `Edit: ${project.name ?? "Untitled"}`
                  : "New Project"
                : project.name
            }
          </div>
          ${
            !props.editMode && project.description
              ? html`<div class="muted" style="font-size:0.85em;">${project.description}</div>`
              : nothing
          }
          ${
            !props.editMode && props.agents.length > 0
              ? html`
                <div style="display:flex;align-items:center;gap:8px;margin-top:4px;">
                  <span class="muted" style="font-size:0.85em;">Agent:</span>
                  <select
                    style="padding:4px 8px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);font-size:0.85em;"
                    @change=${(e: Event) => {
                      const v = (e.target as HTMLSelectElement).value;
                      props.onUpdateProjectAgent((project as ProjectEntry).id, v || null);
                    }}
                  >
                    ${props.agents.map((a) => {
                      const label = a.identity?.name?.trim() || a.name?.trim() || a.id;
                      const current = (project as ProjectEntry).agentId || "";
                      return html`<option value=${a.id} ?selected=${a.id === current}>${label}</option>`;
                    })}
                  </select>
                </div>
              `
              : !props.editMode && (project as ProjectEntry).agentId
                ? html`<div style="margin-top:4px;"><span class="muted" style="font-size:0.85em;">Agent: ${(project as ProjectEntry).agentId}</span></div>`
                : nothing
          }
        </div>
        ${
          !props.editMode
            ? html`
            <div style="display:flex;gap:6px;">
              <button class="btn btn--sm" @click=${() => props.onEditProject((project as ProjectEntry).id)}>Edit</button>
              <button class="btn btn--sm" @click=${() => props.onToggleActive((project as ProjectEntry).id, !(project as ProjectEntry).isActive)}>
                ${(project as ProjectEntry).isActive ? "Deactivate" : "Activate"}
              </button>
              <button class="btn btn--sm" style="color:var(--danger,#e55);" @click=${() => {
                if (
                  confirm(
                    `Delete project "${(project as ProjectEntry).name}"? This cannot be undone.`,
                  )
                ) {
                  props.onDeleteProject((project as ProjectEntry).id);
                }
              }}>Delete</button>
            </div>`
            : nothing
        }
      </div>

      ${props.editMode ? renderEditForm(props) : renderDetailTabs(props)}
    </div>
  `;
}

// --------------- Native Directory Picker Helper ---------------

/**
 * Open the native OS directory picker (Finder on macOS).
 * Returns the selected folder name (NOT the absolute path — browser security limitation).
 * The user must confirm/enter the full absolute path manually.
 */
async function pickNativeDirectory(): Promise<string | null> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handle = await (window as any).showDirectoryPicker({ mode: "read" });
    return handle.name ?? null;
  } catch {
    // User cancelled the picker
    return null;
  }
}

// --------------- Folder Path List (Edit Form) ---------------

function renderFolderPathList(
  props: ProjectsProps,
  target: "knowledgeBasePaths" | "workspacePaths",
  entries: FolderEntry[] | undefined,
  placeholder: string,
) {
  const list = entries ?? [];
  return html`
    <div style="margin-top:6px;">
      ${list.map(
        (entry, i) => html`
          <div style="display:flex;gap:6px;align-items:center;margin-bottom:6px;">
            <input type="text" .value=${entry.label ?? ""}
              @input=${(e: Event) => {
                const newLabel = (e.target as HTMLInputElement).value;
                props.onUpdateFolderPath(target, i, { ...entry, label: newLabel });
              }}
              placeholder="Label (optional)"
              style="${INPUT_STYLE};flex:0 0 140px;font-size:0.85em;" />
            <input type="text" .value=${entry.path}
              @input=${(e: Event) => {
                const newPath = (e.target as HTMLInputElement).value;
                props.onUpdateFolderPath(target, i, { ...entry, path: newPath });
              }}
              placeholder=${placeholder}
              style="${INPUT_STYLE};flex:1;" />
            ${
              HAS_DIRECTORY_PICKER
                ? html`
              <button class="btn btn--sm" @click=${async () => {
                const name = await pickNativeDirectory();
                if (name) {
                  // Show folder name as hint — user must confirm/type absolute path
                  const currentPath = entry.path;
                  const newPath = currentPath || name;
                  props.onUpdateFolderPath(target, i, {
                    ...entry,
                    path: newPath,
                    label: entry.label || name,
                  });
                }
              }} style="white-space:nowrap;font-size:0.8em;">Browse...</button>
            `
                : nothing
            }
            <button class="btn btn--sm" @click=${() => props.onRemoveFolderPath(target, i)}
              style="color:var(--danger,#e55);font-size:0.8em;padding:4px 8px;" title="Remove this path">X</button>
          </div>`,
      )}
      <button class="btn btn--sm" @click=${() => props.onAddFolderPath(target, { path: "", label: "" })}
        style="font-size:0.8em;margin-top:2px;">
        + Add Path
      </button>
    </div>
  `;
}

// --------------- Edit Form ---------------

function renderEditForm(props: ProjectsProps) {
  const project = props.editForm;
  if (!project) return nothing;
  return html`
    <div class="form-grid" style="display:grid;gap:12px;">
      <div class="field">
        <label>Name</label>
        <input type="text" .value=${project.name ?? ""}
          @input=${(e: Event) => props.onFormChange("name", (e.target as HTMLInputElement).value)}
          placeholder="Project name" style=${INPUT_STYLE} />
      </div>
      <div class="field">
        <label>Slug <span class="muted">(folder name, auto-derived from name if blank)</span></label>
        <input type="text" .value=${(project as Record<string, unknown>).slug ?? ""}
          @input=${(e: Event) => props.onFormChange("slug", (e.target as HTMLInputElement).value)}
          placeholder="my-project" style=${INPUT_STYLE} />
      </div>
      <div class="field">
        <label>Description</label>
        <input type="text" .value=${project.description ?? ""}
          @input=${(e: Event) => props.onFormChange("description", (e.target as HTMLInputElement).value)}
          placeholder="Brief description" style=${INPUT_STYLE} />
      </div>
      <div class="field">
        <label>Agent</label>
        <select
          @change=${(e: Event) => props.onFormChange("agentId", (e.target as HTMLSelectElement).value)}
          style=${INPUT_STYLE}
        >
          ${props.agents.map((a) => {
            const label = a.identity?.name?.trim() || a.name?.trim() || a.id;
            const current = (project as Record<string, unknown>).agentId || "";
            return html`<option value=${a.id} ?selected=${a.id === current}>${label}</option>`;
          })}
        </select>
      </div>
      <div class="field">
        <label>System Prompt</label>
        <textarea .value=${project.systemPrompt ?? ""}
          @input=${(e: Event) => props.onFormChange("systemPrompt", (e.target as HTMLTextAreaElement).value)}
          placeholder="Instructions for the AI agent in this project..."
          rows="8" style=${TEXTAREA_STYLE}></textarea>
      </div>

      <!-- Local Folders -->
      <div style="border-top:1px solid var(--border);padding-top:12px;margin-top:4px;">
        <strong style="font-size:0.9em;">Local Folders</strong>
        <div class="muted" style="font-size:0.8em;margin-bottom:12px;">Connect local filesystem folders to this project</div>

        <!-- Knowledge Base Paths -->
        <div style="margin-bottom:12px;">
          <label style="font-weight:600;font-size:0.85em;">Knowledge Base Paths <span class="muted" style="font-weight:normal;">(auto-ingested into RAG)</span></label>
          ${renderFolderPathList(props, "knowledgeBasePaths", (project as Record<string, unknown>).knowledgeBasePaths as FolderEntry[] | undefined, "/path/to/knowledge-base")}
        </div>

        <!-- Workspace Paths -->
        <div>
          <label style="font-weight:600;font-size:0.85em;">Workspace Paths <span class="muted" style="font-weight:normal;">(agent can read/write directly)</span></label>
          ${renderFolderPathList(props, "workspacePaths", (project as Record<string, unknown>).workspacePaths as FolderEntry[] | undefined, "/path/to/workspace")}
        </div>
      </div>

      <div style="display:flex;gap:8px;">
        <button class="btn btn--sm primary" @click=${props.onSaveProject}>Save</button>
        <button class="btn btn--sm" @click=${props.onCancelEdit}>Cancel</button>
      </div>
    </div>
  `;
}

// --------------- Detail Tabs ---------------

function renderDetailTabs(props: ProjectsProps) {
  const p = props.selectedProject as ProjectEntry;
  return html`
    <!-- Tab Bar -->
    <div style="display:flex;gap:2px;margin-bottom:16px;border-bottom:1px solid var(--border);overflow-x:auto;">
      ${DETAIL_TABS.map(
        (tab) => html`
          <button
            class="btn btn--sm ${props.detailPanel === tab.key ? "primary" : ""}"
            style="border-radius:4px 4px 0 0;border-bottom:${props.detailPanel === tab.key ? "2px solid var(--accent,#4a9)" : "2px solid transparent"};white-space:nowrap;"
            @click=${() => props.onSetDetailPanel(tab.key)}
          >${tab.label}</button>
        `,
      )}
    </div>

    <!-- Tab Content -->
    ${props.detailPanel === "overview" ? renderOverviewTab(p) : nothing}
    ${props.detailPanel === "users" ? renderUsersTab(props) : nothing}
    ${props.detailPanel === "contacts" ? renderContactsTab(props) : nothing}
    ${props.detailPanel === "skills" ? renderSkillsTab(props) : nothing}
    ${props.detailPanel === "tasks" ? renderTasksTab(props) : nothing}
    ${props.detailPanel === "cron" ? renderCronTab(props) : nothing}
    ${props.detailPanel === "sessions" ? renderSessionsTab(props) : nothing}
    ${props.detailPanel === "knowledge-base" ? renderKnowledgeBaseTab(props, p) : nothing}
    ${props.detailPanel === "workspace" ? renderWorkspaceTab(props, p) : nothing}
  `;
}

// --------------- Overview Tab ---------------

function renderOverviewTab(p: ProjectEntry) {
  return html`
    <!-- Stats -->
    <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:16px;">
      ${[
        { label: "Users", value: p.userCount ?? 0 },
        { label: "Tasks", value: p.taskCount ?? 0 },
        { label: "Skills", value: p.skills?.length ?? 0 },
      ].map(
        (s) => html`
          <div style="text-align:center;padding:12px;background:var(--bg-inset);border-radius:8px;">
            <div style="font-size:1.5em;font-weight:600;">${s.value}</div>
            <div class="muted" style="font-size:0.8em;">${s.label}</div>
          </div>
        `,
      )}
    </div>

    <!-- Project Info Grid -->
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:16px;font-size:0.85em;">
      ${p.slug ? html`<div><strong>Slug:</strong> <code>${p.slug}</code></div>` : nothing}
      ${p.agentId ? html`<div><strong>Agent:</strong> <code>${p.agentId}</code></div>` : nothing}
      ${p.privatePath ? html`<div><strong>Private folder:</strong> <code style="word-break:break-all;">${p.privatePath}</code></div>` : nothing}
      ${p.publicPath ? html`<div><strong>Public folder:</strong> <code style="word-break:break-all;">${p.publicPath}</code></div>` : nothing}
      ${p.knowledgeBasePath ? html`<div><strong>Knowledge Base:</strong> <code style="word-break:break-all;">${p.knowledgeBasePath}</code></div>` : nothing}
      ${p.workspacePath ? html`<div><strong>Workspace:</strong> <code style="word-break:break-all;">${p.workspacePath}</code></div>` : nothing}
      <div><strong>Status:</strong> <span class="pill pill--sm ${p.isActive ? "pill--ok" : ""}">${p.isActive ? "Active" : "Inactive"}</span></div>
      <div><strong>Created:</strong> ${p.createdAt.split("T")[0]}</div>
    </div>

    <!-- System Prompt -->
    <div>
      <strong>System Prompt:</strong>
      <pre style="background:var(--bg-inset);padding:12px;border-radius:4px;font-size:0.85em;overflow:auto;max-height:300px;white-space:pre-wrap;margin-top:4px;">${p.systemPrompt || "(none)"}</pre>
    </div>
  `;
}

// --------------- Users Tab ---------------

function renderUsersTab(props: ProjectsProps) {
  const { projectUsers, projectUsersLoading, allUsers } = props;
  const assignedIds = new Set(projectUsers.map((u) => u.userId));
  const unassigned = allUsers.filter((u) => !assignedIds.has(u.id));

  return html`
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
      <strong>Assigned Users <span class="muted">(${projectUsers.length})</span></strong>
    </div>
    ${
      projectUsersLoading
        ? html`
            <div class="muted">Loading...</div>
          `
        : nothing
    }

    ${
      projectUsers.length > 0
        ? html`
        <div style="max-height:300px;overflow:auto;margin-bottom:16px;">
          <table style="width:100%;border-collapse:collapse;font-size:0.85em;">
            <thead>
              <tr style="border-bottom:1px solid var(--border);text-align:left;">
                <th style="padding:6px 8px;">Name</th>
                <th style="padding:6px 8px;">Email</th>
                <th style="padding:6px 8px;">Role</th>
                <th style="padding:6px 8px;">Actions</th>
              </tr>
            </thead>
            <tbody>
              ${projectUsers.map(
                (pu) => html`
                  <tr style="border-bottom:1px solid var(--border);">
                    <td style="padding:6px 8px;">${puDisplayName(pu) || pu.userId.slice(0, 8)}</td>
                    <td style="padding:6px 8px;">${pu.email ?? "-"}</td>
                    <td style="padding:6px 8px;">
                      <select style="padding:4px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);"
                        .value=${pu.role}
                        @change=${(e: Event) => props.onChangeUserRole(pu.userId, (e.target as HTMLSelectElement).value as "admin" | "project_admin" | "user")}>
                        <option value="user">User</option>
                        <option value="project_admin">Project Admin</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td style="padding:6px 8px;">
                      <button class="btn btn--sm" style="color:var(--danger,#e55);font-size:0.8em;"
                        @click=${() => {
                          if (confirm(`Remove ${puDisplayName(pu) || "this user"} from project?`))
                            props.onRemoveUser(pu.userId);
                        }}>
                        Remove
                      </button>
                    </td>
                  </tr>
                `,
              )}
            </tbody>
          </table>
        </div>`
        : !projectUsersLoading
          ? html`
              <div class="muted" style="padding: 8px 0; font-size: 0.85em">No users assigned yet.</div>
            `
          : nothing
    }

    <!-- Add user to project -->
    ${
      unassigned.length > 0
        ? html`
        <div style="border-top:1px solid var(--border);padding-top:12px;">
          <strong style="font-size:0.85em;">Add User to Project</strong>
          <div style="display:flex;gap:8px;margin-top:8px;align-items:center;">
            <select id="add-user-select" style="flex:1;padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);">
              ${unassigned.map((u) => html`<option value=${u.id}>${puDisplayName(u) || u.email || u.id.slice(0, 8)}</option>`)}
            </select>
            <select id="add-user-role" style="padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);">
              <option value="user">User</option>
              <option value="project_admin">Project Admin</option>
              <option value="admin">Admin</option>
            </select>
            <button class="btn btn--sm primary" @click=${() => {
              const sel = document.getElementById("add-user-select") as HTMLSelectElement;
              const role = document.getElementById("add-user-role") as HTMLSelectElement;
              if (sel?.value)
                props.onAddUser(sel.value, role.value as "admin" | "project_admin" | "user");
            }}>Add</button>
          </div>
        </div>`
        : nothing
    }
  `;
}

// --------------- Contacts Tab ---------------

const CONTACT_CHANNELS = [
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "telegram", label: "Telegram" },
  { value: "sms", label: "SMS" },
  { value: "webchat", label: "Webchat" },
  { value: "nextcloud", label: "Nextcloud" },
];

const CHANNEL_SELECT_STYLE =
  "width:100%;padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);";

/** Read the resolved channel value from a select + optional custom input pair. */
function readChannelValue(selectId: string, customId: string): string {
  const sel = document.getElementById(selectId) as HTMLSelectElement | null;
  if (sel?.value === "__other__") {
    return (document.getElementById(customId) as HTMLInputElement)?.value.trim() ?? "";
  }
  return sel?.value ?? "";
}

/** Toggle visibility of the custom channel input when "Other" is selected. */
function onChannelSelectChange(selectId: string, customId: string): void {
  const sel = document.getElementById(selectId) as HTMLSelectElement | null;
  const custom = document.getElementById(customId) as HTMLElement | null;
  if (custom) {
    custom.style.display = sel?.value === "__other__" ? "block" : "none";
  }
}

function renderContactsTab(props: ProjectsProps) {
  const { projectContacts, projectContactsLoading, contactCreateMode, contactEditingId } = props;

  return html`
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
      <strong>Project Contacts <span class="muted">(${projectContacts.length})</span></strong>
      ${
        !contactCreateMode
          ? html`<button class="btn btn--sm primary" @click=${() => props.onStartCreateContact()}>+ Add Contact</button>`
          : nothing
      }
    </div>
    ${
      projectContactsLoading
        ? html`
            <div class="muted">Loading...</div>
          `
        : nothing
    }

    ${contactCreateMode ? renderContactForm(props) : nothing}

    ${
      projectContacts.length > 0
        ? html`
      <div style="max-height:400px;overflow:auto;margin-bottom:16px;">
        <table style="width:100%;border-collapse:collapse;font-size:0.85em;">
          <thead>
            <tr style="border-bottom:1px solid var(--border);text-align:left;">
              <th style="padding:6px 8px;">Name</th>
              <th style="padding:6px 8px;">Channel</th>
              <th style="padding:6px 8px;">Address</th>
              <th style="padding:6px 8px;">Tags</th>
              <th style="padding:6px 8px;">Status</th>
              <th style="padding:6px 8px;">Actions</th>
            </tr>
          </thead>
          <tbody>
            ${projectContacts.map((c) =>
              contactEditingId === c.id
                ? renderContactEditRow(props, c)
                : renderContactRow(props, c),
            )}
          </tbody>
        </table>
      </div>`
        : !projectContactsLoading && !contactCreateMode
          ? html`
              <div class="muted" style="padding: 8px 0; font-size: 0.85em">No contacts yet.</div>
            `
          : nothing
    }
  `;
}

function renderContactRow(props: ProjectsProps, c: ProjectContactEntry) {
  return html`
    <tr style="border-bottom:1px solid var(--border);${!c.isActive ? "opacity:0.5;" : ""}">
      <td style="padding:6px 8px;font-weight:500;">${c.name}</td>
      <td style="padding:6px 8px;"><span class="pill">${c.channel}</span></td>
      <td style="padding:6px 8px;font-family:monospace;font-size:0.9em;">${c.address}</td>
      <td style="padding:6px 8px;">
        ${
          c.tags.length > 0
            ? c.tags.map(
                (t) => html`<span class="pill pill--info" style="margin:1px 2px;">${t}</span>`,
              )
            : html`
                <span class="muted">-</span>
              `
        }
      </td>
      <td style="padding:6px 8px;">
        <span class="pill ${c.isActive ? "pill--ok" : ""}">${c.isActive ? "Active" : "Inactive"}</span>
      </td>
      <td style="padding:6px 8px;white-space:nowrap;">
        <button class="btn btn--sm" style="font-size:0.8em;margin-right:4px;"
          @click=${() => props.onStartEditContact(c.id)}>Edit</button>
        <button class="btn btn--sm" style="font-size:0.8em;margin-right:4px;"
          @click=${() => props.onUpdateContact(c.id, { isActive: !c.isActive })}>
          ${c.isActive ? "Deactivate" : "Activate"}
        </button>
        <button class="btn btn--sm" style="color:var(--danger,#e55);font-size:0.8em;"
          @click=${() => {
            if (confirm(`Delete contact "${c.name}"? This cannot be undone.`)) {
              props.onDeleteContact(c.id);
            }
          }}>Delete</button>
      </td>
    </tr>
    ${
      c.notes
        ? html`
      <tr style="border-bottom:1px solid var(--border);">
        <td colspan="6" style="padding:2px 8px 6px 8px;font-size:0.8em;" class="muted">
          ${c.notes}
        </td>
      </tr>`
        : nothing
    }
  `;
}

function renderContactEditRow(props: ProjectsProps, c: ProjectContactEntry) {
  const nameId = `contact-edit-name-${c.id}`;
  const channelId = `contact-edit-channel-${c.id}`;
  const addressId = `contact-edit-address-${c.id}`;
  const tagsId = `contact-edit-tags-${c.id}`;
  const notesId = `contact-edit-notes-${c.id}`;

  return html`
    <tr style="border-bottom:1px solid var(--border);background:var(--bg-inset);">
      <td style="padding:6px 8px;">
        <input id=${nameId} type="text" .value=${c.name}
          style="width:100%;padding:4px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);" />
      </td>
      <td style="padding:6px 8px;">
        ${(() => {
          const customId = `contact-edit-channel-custom-${c.id}`;
          const isKnown = CONTACT_CHANNELS.some((ch) => ch.value === c.channel);
          return html`
            <select id=${channelId}
              style="width:100%;padding:4px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);"
              @change=${() => onChannelSelectChange(channelId, customId)}>
              ${CONTACT_CHANNELS.map((ch) => html`<option value=${ch.value} ?selected=${ch.value === c.channel}>${ch.label}</option>`)}
              <option value="__other__" ?selected=${!isKnown}>Other...</option>
            </select>
            <input id=${customId} type="text" .value=${isKnown ? "" : c.channel}
              placeholder="Custom channel"
              style="width:100%;padding:4px;margin-top:3px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);display:${isKnown ? "none" : "block"};" />
          `;
        })()}
      </td>
      <td style="padding:6px 8px;">
        <input id=${addressId} type="text" .value=${c.address}
          style="width:100%;padding:4px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);" />
      </td>
      <td style="padding:6px 8px;">
        <input id=${tagsId} type="text" .value=${c.tags.join(", ")}
          placeholder="tag1, tag2"
          style="width:100%;padding:4px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);" />
      </td>
      <td colspan="2" style="padding:6px 8px;white-space:nowrap;">
        <button class="btn btn--sm primary" style="font-size:0.8em;margin-right:4px;"
          @click=${() => {
            const name = (document.getElementById(nameId) as HTMLInputElement)?.value.trim();
            const channel = readChannelValue(channelId, `contact-edit-channel-custom-${c.id}`);
            const address = (document.getElementById(addressId) as HTMLInputElement)?.value.trim();
            const tagsRaw = (document.getElementById(tagsId) as HTMLInputElement)?.value ?? "";
            const notes =
              (document.getElementById(notesId) as HTMLTextAreaElement)?.value?.trim() ?? null;
            const tags = tagsRaw
              .split(",")
              .map((t) => t.trim())
              .filter(Boolean);
            props.onUpdateContact(c.id, { name, channel, address, tags, notes: notes || null });
          }}>Save</button>
        <button class="btn btn--sm" style="font-size:0.8em;"
          @click=${() => props.onCancelContactForm()}>Cancel</button>
      </td>
    </tr>
    <tr style="border-bottom:1px solid var(--border);background:var(--bg-inset);">
      <td colspan="6" style="padding:2px 8px 6px 8px;">
        <textarea id=${notesId} rows="2" placeholder="Notes (optional)"
          style="width:100%;padding:4px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);font-size:0.85em;resize:vertical;"
        >${c.notes ?? ""}</textarea>
      </td>
    </tr>
  `;
}

function renderContactForm(props: ProjectsProps) {
  return html`
    <div style="border:1px solid var(--border);border-radius:6px;padding:12px;margin-bottom:16px;background:var(--bg-inset);">
      <strong style="font-size:0.85em;">New Contact</strong>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:8px;">
        <div>
          <label style="font-size:0.8em;display:block;margin-bottom:2px;">Name *</label>
          <input id="contact-new-name" type="text" placeholder="John Doe"
            style="width:100%;padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);" />
        </div>
        <div>
          <label style="font-size:0.8em;display:block;margin-bottom:2px;">Channel *</label>
          <select id="contact-new-channel" style=${CHANNEL_SELECT_STYLE}
            @change=${() => onChannelSelectChange("contact-new-channel", "contact-new-channel-custom")}>
            ${CONTACT_CHANNELS.map((ch) => html`<option value=${ch.value}>${ch.label}</option>`)}
            <option value="__other__">Other...</option>
          </select>
          <input id="contact-new-channel-custom" type="text" placeholder="Custom channel name"
            style="width:100%;padding:6px;margin-top:4px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);display:none;" />
        </div>
        <div>
          <label style="font-size:0.8em;display:block;margin-bottom:2px;">Address *</label>
          <input id="contact-new-address" type="text" placeholder="email@example.com, +1234567890..."
            style="width:100%;padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);" />
        </div>
        <div>
          <label style="font-size:0.8em;display:block;margin-bottom:2px;">Tags</label>
          <input id="contact-new-tags" type="text" placeholder="accountant, ceo, support (comma-separated)"
            style="width:100%;padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);" />
        </div>
      </div>
      <div style="margin-top:8px;">
        <label style="font-size:0.8em;display:block;margin-bottom:2px;">Notes</label>
        <textarea id="contact-new-notes" rows="2" placeholder="Optional notes about this contact"
          style="width:100%;padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);font-size:0.85em;resize:vertical;"></textarea>
      </div>
      <div style="display:flex;gap:8px;margin-top:10px;">
        <button class="btn btn--sm primary" @click=${() => {
          const name = (
            document.getElementById("contact-new-name") as HTMLInputElement
          )?.value.trim();
          const channel = readChannelValue("contact-new-channel", "contact-new-channel-custom");
          const address = (
            document.getElementById("contact-new-address") as HTMLInputElement
          )?.value.trim();
          const tagsRaw =
            (document.getElementById("contact-new-tags") as HTMLInputElement)?.value ?? "";
          const notes =
            (document.getElementById("contact-new-notes") as HTMLTextAreaElement)?.value?.trim() ??
            "";
          if (!name || !channel || !address) {
            alert("Name, channel, and address are required.");
            return;
          }
          const tags = tagsRaw
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean);
          props.onCreateContact({ name, channel, address, tags, notes });
        }}>Create</button>
        <button class="btn btn--sm" @click=${() => props.onCancelContactForm()}>Cancel</button>
      </div>
    </div>
  `;
}

// --------------- Skills Tab ---------------

function renderSkillsTab(props: ProjectsProps) {
  const { projectSkills, projectSkillsLoading } = props;
  const avatarSkill = projectSkills.find(
    (s) => s.name === "avatar" || (s.metadata as Record<string, unknown>)?.isAvatar === true,
  );
  const hasAvatar = !!avatarSkill?.isActive;
  const regularSkills = projectSkills.filter(
    (s) => s.name !== "avatar" && (s.metadata as Record<string, unknown>)?.isAvatar !== true,
  );

  return html`
    <!-- Avatar Capability Toggle -->
    <div style="padding:12px;border:1px solid var(--border);border-radius:6px;margin-bottom:16px;display:flex;justify-content:space-between;align-items:center;background:var(--bg-inset);">
      <div>
        <strong>Avatar Capability</strong>
        <div class="muted" style="font-size:0.8em;margin-top:2px;">Enable LiveKit + SadTalker avatar video calls for this project</div>
      </div>
      <button class="btn btn--sm ${hasAvatar ? "" : "primary"}"
        @click=${() => props.onToggleAvatar(!hasAvatar)}>
        ${hasAvatar ? "Disable Avatar" : "Enable Avatar"}
      </button>
    </div>

    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
      <strong>Project Skills <span class="muted">(${regularSkills.length})</span></strong>
    </div>
    ${
      projectSkillsLoading
        ? html`
            <div class="muted">Loading...</div>
          `
        : nothing
    }

    ${
      regularSkills.length > 0
        ? html`
        <div style="display:grid;gap:8px;">
          ${regularSkills.map(
            (s) => html`
              <div style="padding:10px;border:1px solid var(--border);border-radius:6px;display:flex;justify-content:space-between;align-items:center;">
                <div>
                  <strong>${s.name}</strong>
                  <span class="pill pill--sm ${s.isActive ? "pill--ok" : ""}" style="margin-left:8px;">${s.isActive ? "Active" : "Inactive"}</span>
                  ${s.description ? html`<div class="muted" style="font-size:0.8em;margin-top:2px;">${s.description}</div>` : nothing}
                </div>
                <div class="muted" style="font-size:0.8em;">v${s.version}</div>
              </div>
            `,
          )}
        </div>`
        : !projectSkillsLoading
          ? html`
              <div class="muted" style="padding: 16px 0; text-align: center; font-size: 0.85em">
                No skills assigned to this project yet.<br />
                <span style="font-size: 0.9em"
                  >Use the MCP Skills tab to create skills and assign them to this project.</span
                >
              </div>
            `
          : nothing
    }
  `;
}

// --------------- Tasks Tab ---------------

function renderTasksTab(props: ProjectsProps) {
  const { projectTasks, projectTasksLoading } = props;
  return html`
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
      <strong>Project Tasks <span class="muted">(${projectTasks.length})</span></strong>
    </div>
    ${
      projectTasksLoading
        ? html`
            <div class="muted">Loading...</div>
          `
        : nothing
    }

    ${
      projectTasks.length > 0
        ? html`
        <div style="max-height:400px;overflow:auto;">
          <table style="width:100%;border-collapse:collapse;font-size:0.85em;">
            <thead>
              <tr style="border-bottom:1px solid var(--border);text-align:left;">
                <th style="padding:6px 8px;">Title</th>
                <th style="padding:6px 8px;">Type</th>
                <th style="padding:6px 8px;">Status</th>
                <th style="padding:6px 8px;">Assigned</th>
                <th style="padding:6px 8px;">Created</th>
              </tr>
            </thead>
            <tbody>
              ${projectTasks.map(
                (t) => html`
                  <tr style="border-bottom:1px solid var(--border);">
                    <td style="padding:6px 8px;">${t.title}</td>
                    <td style="padding:6px 8px;"><span class="pill pill--sm">${t.type}</span></td>
                    <td style="padding:6px 8px;">
                      <span class="pill pill--sm ${STATUS_COLORS[t.status] ?? ""}">${t.status}</span>
                    </td>
                    <td style="padding:6px 8px;">${t.assignedUserName ?? "-"}</td>
                    <td style="padding:6px 8px;">${t.createdAt.split("T")[0]}</td>
                  </tr>
                `,
              )}
            </tbody>
          </table>
        </div>`
        : !projectTasksLoading
          ? html`
              <div class="muted" style="padding: 16px 0; text-align: center; font-size: 0.85em">
                No tasks for this project yet.<br />
                <span style="font-size: 0.9em">Use the Tasks tab to create tasks scoped to this project.</span>
              </div>
            `
          : nothing
    }
  `;
}

// --------------- Cron Jobs Tab (read-only) ---------------

function formatPayloadJson(payload: unknown): string {
  try {
    return JSON.stringify(payload ?? {}, null, 2);
  } catch {
    return "{}";
  }
}

function renderCronTab(props: ProjectsProps) {
  const { projectCronJobs, projectCronJobsLoading } = props;

  return html`
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
      <strong>Cron Jobs <span class="muted">(${projectCronJobs.length})</span></strong>
    </div>
    ${
      projectCronJobsLoading
        ? html`
            <div class="muted">Loading...</div>
          `
        : nothing
    }

    ${
      projectCronJobs.length > 0
        ? html`
        <div style="display:grid;gap:8px;">
          ${projectCronJobs.map(
            (job) => html`
              <div style="padding:10px;border:1px solid var(--border);border-radius:6px;">
                <div style="display:flex;justify-content:space-between;align-items:center;">
                  <div>
                    <strong>${job.name}</strong>
                    <span class="pill pill--sm ${job.isActive ? "pill--ok" : ""}" style="margin-left:8px;">${job.isActive ? "Active" : "Paused"}</span>
                  </div>
                </div>
                <div class="muted" style="font-size:0.8em;margin-top:6px;display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:4px;">
                  <span>Schedule: <code>${job.cronExpression}</code></span>
                  <span>Timezone: ${job.timezone}</span>
                  <span>Trigger: <span class="chip" style="font-size:0.9em;">${job.trigger}</span></span>
                  ${job.nextRunAt ? html`<span>Next run: ${formatTimestamp(job.nextRunAt)}</span>` : nothing}
                  ${job.lastRunAt ? html`<span>Last run: ${formatTimestamp(job.lastRunAt)}</span>` : nothing}
                </div>
                ${
                  job.payload && Object.keys(job.payload).length > 0
                    ? html`
                    <details style="margin-top:8px;">
                      <summary class="muted" style="font-size:0.8em;cursor:pointer;">Payload</summary>
                      <pre class="mono" style="font-size:0.75em;margin-top:4px;padding:8px;background:var(--bg);border-radius:4px;overflow-x:auto;">${formatPayloadJson(job.payload)}</pre>
                    </details>`
                    : nothing
                }
              </div>
            `,
          )}
        </div>`
        : !projectCronJobsLoading
          ? html`
              <div class="muted" style="padding: 16px 0; text-align: center; font-size: 0.85em">
                No cron jobs configured for this project.<br />
                <span style="font-size: 0.9em"
                  >Use the Cron tab in the sidebar to create and manage cron jobs.</span
                >
              </div>
            `
          : nothing
    }
  `;
}

function formatTimestamp(ts: string | null): string {
  if (!ts) {
    return "-";
  }
  try {
    const d = new Date(ts);
    return d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
  } catch {
    return ts.split("T")[0] ?? ts;
  }
}

// --------------- Sessions Tab ---------------

function renderSessionsTab(props: ProjectsProps) {
  const { sessions, sessionsLoading, onCompleteSession, sessionCreateMode } = props;
  return html`
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
      <strong>Chat Sessions <span class="muted">(${sessions.length})</span></strong>
      ${
        !sessionCreateMode
          ? html`<button class="btn btn--sm primary" @click=${props.onCreateSession}>+ New Session</button>`
          : nothing
      }
    </div>
    ${
      sessionsLoading
        ? html`
            <div class="muted">Loading...</div>
          `
        : nothing
    }

    <!-- Create Session Form -->
    ${
      sessionCreateMode
        ? html`
        <div style="border:1px solid var(--border);border-radius:6px;padding:12px;margin-bottom:12px;display:grid;gap:10px;">
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
            <div class="field">
              <label style="font-size:0.85em;">Type</label>
              <select id="new-session-type" style="padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);">
                <option value="chat">Chat</option>
                <option value="voice">Voice</option>
              </select>
            </div>
            <div class="field">
              <label style="font-size:0.85em;">Channel</label>
              <select id="new-session-channel" style="padding:6px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);">
                <option value="webchat">Webchat</option>
                <option value="telegram">Telegram</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="email">Email</option>
              </select>
            </div>
          </div>
          <div style="display:flex;gap:8px;">
            <button class="btn btn--sm primary" @click=${() => {
              const type = (document.getElementById("new-session-type") as HTMLSelectElement)
                ?.value as "chat" | "voice";
              const channel = (document.getElementById("new-session-channel") as HTMLSelectElement)
                ?.value;
              props.onSaveNewSession(type, channel);
            }}>Create</button>
            <button class="btn btn--sm" @click=${props.onCancelCreateSession}>Cancel</button>
          </div>
        </div>`
        : nothing
    }

    <!-- Sessions Table -->
    ${
      sessions.length > 0
        ? html`
        <div style="max-height:400px;overflow:auto;">
          <table style="width:100%;border-collapse:collapse;font-size:0.85em;">
            <thead>
              <tr style="border-bottom:1px solid var(--border);text-align:left;">
                <th style="padding:6px 8px;">Type</th>
                <th style="padding:6px 8px;">Channel</th>
                <th style="padding:6px 8px;">Status</th>
                <th style="padding:6px 8px;">User</th>
                <th style="padding:6px 8px;">Created</th>
                <th style="padding:6px 8px;">Actions</th>
              </tr>
            </thead>
            <tbody>
              ${sessions.map(
                (s) => html`
                  <tr style="border-bottom:1px solid var(--border);">
                    <td style="padding:6px 8px;">${s.type}</td>
                    <td style="padding:6px 8px;">${s.channel ?? "-"}</td>
                    <td style="padding:6px 8px;">
                      <span class="pill pill--sm ${STATUS_COLORS[s.status] ?? ""}">${s.status}</span>
                    </td>
                    <td style="padding:6px 8px;">${s.userName ?? s.userId ?? "-"}</td>
                    <td style="padding:6px 8px;">${s.createdAt.replace("T", " ").slice(0, 19)}</td>
                    <td style="padding:6px 8px;">
                      ${
                        s.status === "active"
                          ? html`<button class="btn btn--sm" style="font-size:0.8em;"
                            @click=${() => onCompleteSession(s.id, "completed")}>Complete</button>`
                          : nothing
                      }
                    </td>
                  </tr>
                `,
              )}
            </tbody>
          </table>
        </div>`
        : !sessionsLoading && !sessionCreateMode
          ? html`
              <div class="muted" style="padding: 16px 0; text-align: center; font-size: 0.85em">
                No chat sessions yet. An admin session is auto-created with new projects.
              </div>
            `
          : nothing
    }
  `;
}

// --------------- Knowledge Base Tab ---------------

function renderKnowledgeBaseTab(props: ProjectsProps, p: ProjectEntry) {
  const { knowledgeBaseSyncing, knowledgeBaseSyncResult } = props;
  const kbPaths = p.knowledgeBasePaths ?? [];

  return html`
    <div style="padding:12px;border:1px solid var(--border);border-radius:6px;margin-bottom:16px;background:var(--bg-inset);">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
        <div>
          <strong>Knowledge Base Folders</strong>
          <div class="muted" style="font-size:0.8em;margin-top:2px;">
            Files in these folders are auto-ingested into the RAG document system for semantic search.
          </div>
        </div>
        ${
          kbPaths.length > 0
            ? html`
          <button class="btn btn--sm primary" @click=${props.onSyncKnowledgeBase} ?disabled=${knowledgeBaseSyncing}>
            ${knowledgeBaseSyncing ? "Syncing..." : "Sync All"}
          </button>
        `
            : nothing
        }
      </div>

      ${
        kbPaths.length > 0
          ? html`
          ${kbPaths.map(
            (entry, i) => html`
            <div style="margin-bottom:10px;padding:8px 10px;border:1px solid var(--border);border-radius:4px;background:var(--bg);">
              <div style="display:flex;justify-content:space-between;align-items:center;">
                <div style="flex:1;min-width:0;">
                  ${entry.label ? html`<strong style="font-size:0.85em;">${entry.label}</strong>` : nothing}
                  <code style="word-break:break-all;font-size:0.8em;display:block;margin-top:${entry.label ? "2px" : "0"};">${entry.path}</code>
                </div>
                <button class="btn btn--sm" @click=${() => props.onRemoveFolderPathDirect("knowledgeBasePaths", i)}
                  style="color:var(--danger,#e55);font-size:0.75em;margin-left:8px;" title="Remove">X</button>
              </div>
            </div>
          `,
          )}

          <div class="muted" style="font-size:0.75em;margin-top:4px;margin-bottom:8px;">
            Supported: PDF, DOCX, PPTX, XLSX, TXT, MD, CSV, JSON, HTML, images (OCR)
          </div>

          ${
            knowledgeBaseSyncResult
              ? html`
              <div style="margin-top:8px;padding:10px;border:1px solid var(--border);border-radius:4px;font-size:0.85em;">
                <div style="display:flex;gap:16px;margin-bottom:4px;flex-wrap:wrap;">
                  <span><strong>${knowledgeBaseSyncResult.totalFiles}</strong> total files</span>
                  <span style="color:var(--accent,#4a9);"><strong>${knowledgeBaseSyncResult.processed}</strong> processed</span>
                  <span class="muted"><strong>${knowledgeBaseSyncResult.skipped}</strong> skipped</span>
                  ${knowledgeBaseSyncResult.failed > 0 ? html`<span style="color:var(--danger,#e55);"><strong>${knowledgeBaseSyncResult.failed}</strong> failed</span>` : nothing}
                </div>
                ${
                  knowledgeBaseSyncResult.errors && knowledgeBaseSyncResult.errors.length > 0
                    ? html`
                    <details style="margin-top:6px;">
                      <summary class="muted" style="cursor:pointer;">Errors</summary>
                      <pre style="font-size:0.8em;margin-top:4px;padding:6px;background:var(--bg);border-radius:4px;overflow-x:auto;">${knowledgeBaseSyncResult.errors.join("\n")}</pre>
                    </details>`
                    : nothing
                }
              </div>`
              : nothing
          }
        `
          : html`
              <div class="muted" style="padding: 12px 0; text-align: center; font-size: 0.85em">
                No knowledge base paths configured.
              </div>
            `
      }

      <!-- Add Path -->
      ${renderAddPathInline(props, "knowledgeBasePaths")}
    </div>
  `;
}

// --------------- Workspace Tab ---------------

function renderWorkspaceTab(props: ProjectsProps, p: ProjectEntry) {
  const { workspaceVerifying, workspaceVerifyResult } = props;
  const wsPaths = p.workspacePaths ?? [];

  return html`
    <div style="padding:12px;border:1px solid var(--border);border-radius:6px;margin-bottom:16px;background:var(--bg-inset);">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
        <div>
          <strong>Agent Workspace Folders</strong>
          <div class="muted" style="font-size:0.8em;margin-top:2px;">
            Local folders the agent can read and write to directly via file system tools.
          </div>
        </div>
        ${
          wsPaths.length > 0
            ? html`
          <button class="btn btn--sm primary" @click=${props.onVerifyWorkspace} ?disabled=${workspaceVerifying}>
            ${workspaceVerifying ? "Verifying..." : "Verify All"}
          </button>
        `
            : nothing
        }
      </div>

      ${
        wsPaths.length > 0
          ? html`
          ${wsPaths.map(
            (entry, i) => html`
            <div style="margin-bottom:10px;padding:8px 10px;border:1px solid var(--border);border-radius:4px;background:var(--bg);">
              <div style="display:flex;justify-content:space-between;align-items:center;">
                <div style="flex:1;min-width:0;">
                  ${entry.label ? html`<strong style="font-size:0.85em;">${entry.label}</strong>` : nothing}
                  <code style="word-break:break-all;font-size:0.8em;display:block;margin-top:${entry.label ? "2px" : "0"};">${entry.path}</code>
                </div>
                <button class="btn btn--sm" @click=${() => props.onRemoveFolderPathDirect("workspacePaths", i)}
                  style="color:var(--danger,#e55);font-size:0.75em;margin-left:8px;" title="Remove">X</button>
              </div>
            </div>
          `,
          )}

          ${
            workspaceVerifyResult
              ? html`
              <div style="margin-top:8px;padding:10px;border:1px solid var(--border);border-radius:4px;font-size:0.85em;">
                <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(120px,1fr));gap:8px;">
                  <div>
                    <strong>Exists:</strong>
                    <span class="pill pill--sm ${workspaceVerifyResult.exists ? "pill--ok" : "pill--danger"}">
                      ${workspaceVerifyResult.exists ? "Yes" : "No"}
                    </span>
                  </div>
                  <div>
                    <strong>Readable:</strong>
                    <span class="pill pill--sm ${workspaceVerifyResult.readable ? "pill--ok" : "pill--danger"}">
                      ${workspaceVerifyResult.readable ? "Yes" : "No"}
                    </span>
                  </div>
                  <div>
                    <strong>Writable:</strong>
                    <span class="pill pill--sm ${workspaceVerifyResult.writable ? "pill--ok" : "pill--danger"}">
                      ${workspaceVerifyResult.writable ? "Yes" : "No"}
                    </span>
                  </div>
                  ${
                    workspaceVerifyResult.fileCount !== undefined
                      ? html`<div><strong>Files:</strong> ${workspaceVerifyResult.fileCount}</div>`
                      : nothing
                  }
                </div>
                ${
                  workspaceVerifyResult.error
                    ? html`<div style="margin-top:6px;color:var(--danger,#e55);font-size:0.85em;">${workspaceVerifyResult.error}</div>`
                    : nothing
                }
              </div>`
              : nothing
          }
        `
          : html`
              <div class="muted" style="padding: 12px 0; text-align: center; font-size: 0.85em">
                No workspace paths configured.
              </div>
            `
      }

      <!-- Add Path -->
      ${renderAddPathInline(props, "workspacePaths")}
    </div>
  `;
}

// --------------- Add Path Inline (Detail Tab) ---------------

/**
 * Renders a small inline form to add a new folder path in the detail tabs.
 * Uses native directory picker when available, with manual path entry as fallback.
 */
function renderAddPathInline(
  props: ProjectsProps,
  target: "knowledgeBasePaths" | "workspacePaths",
) {
  const inputId = `add-path-${target}`;
  const labelId = `add-label-${target}`;
  return html`
    <div style="margin-top:8px;padding:8px;border:1px dashed var(--border);border-radius:4px;">
      <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;">
        <input id=${labelId} type="text" placeholder="Label (optional)"
          style="${INPUT_STYLE};flex:0 0 130px;font-size:0.8em;" />
        <input id=${inputId} type="text" placeholder="/absolute/path/to/folder"
          style="${INPUT_STYLE};flex:1;min-width:200px;font-size:0.8em;" />
        ${
          HAS_DIRECTORY_PICKER
            ? html`
          <button class="btn btn--sm" style="font-size:0.75em;white-space:nowrap;" @click=${async () => {
            const name = await pickNativeDirectory();
            if (name) {
              const pathInput = document.getElementById(inputId) as HTMLInputElement | null;
              const labelInput = document.getElementById(labelId) as HTMLInputElement | null;
              if (pathInput && !pathInput.value) {
                pathInput.value = name;
              }
              if (labelInput && !labelInput.value) {
                labelInput.value = name;
              }
            }
          }}>Browse...</button>
        `
            : nothing
        }
        <button class="btn btn--sm primary" style="font-size:0.75em;white-space:nowrap;" @click=${() => {
          const pathInput = document.getElementById(inputId) as HTMLInputElement | null;
          const labelInput = document.getElementById(labelId) as HTMLInputElement | null;
          const pathVal = pathInput?.value.trim();
          if (!pathVal) {
            return;
          }
          const labelVal = labelInput?.value.trim();
          props.onAddFolderPathDirect(target, {
            path: pathVal,
            label: labelVal || undefined,
          });
          if (pathInput) {
            pathInput.value = "";
          }
          if (labelInput) {
            labelInput.value = "";
          }
        }}>+ Add</button>
      </div>
    </div>
  `;
}
